'use client';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion2';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Separator } from '@/components/ui/separator';
import {
  getScheduledWindows,
  isSchedulableInteractionType,
  secondsToHms,
} from '@/lib/evergreenInteractionSchedule';
import HmsInputs from './HmsInputs';
import ScheduledWindowsEditor from './ScheduledWindowsEditor';
import { cn } from '@/lib/utils';
import { useAppSelector } from '@/app/hooks';
import { useVideoFallbackSource } from '@/hooks/useVideoFallbackSource';
import type { RootState } from '@/app/store';
import {
  buildTimelineSegments,
  isDisplayableTimelineEvent,
  type TimelineDisplayEventType,
} from '@/features/webinar/evergreenTimelineUtils';

function secondsToTimeString(seconds: number): string {
  if (!seconds || !Number.isFinite(seconds)) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export type TimeCodeScheduling = {
  workspaceId: number;
  webinarId: number;
  durationSeconds: number | null;
};

type TimeCodeComponentProps = {
  videoUrl: string;
  videoFallbackUrl?: string;
  selectedEventType?: TimelineDisplayEventType;
  selectedResourceId?: number | null;
  readOnly?: boolean;
  /** Présent sur un Evergreen MP4 : les créneaux deviennent éditables. */
  scheduling?: TimeCodeScheduling;
};

export default function TimeCodeComponent({
  videoUrl,
  videoFallbackUrl,
  selectedEventType,
  selectedResourceId,
  readOnly,
  scheduling,
}: TimeCodeComponentProps) {
  const { t } = useTranslation('createWebinar');
  const locked = readOnly === true;
  const videoRef = useRef<HTMLVideoElement>(null);
  const { src: videoSrc, onError: onVideoError } = useVideoFallbackSource(
    videoUrl,
    videoFallbackUrl,
  );
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  const evergreenTimeline = useAppSelector((state: RootState) => state.webinar.evergreenTimeline);
  const timelineEvents = useMemo(
    () => evergreenTimeline.filter(isDisplayableTimelineEvent),
    [evergreenTimeline],
  );

  const segments = useMemo(
    () => buildTimelineSegments(timelineEvents, videoDuration),
    [timelineEvents, videoDuration],
  );

  const segmentsForResource = useMemo(() => {
    if (selectedEventType != null && selectedResourceId != null) {
      return segments.filter(
        (s) => s.eventType === selectedEventType && s.resourceId === selectedResourceId,
      );
    }
    return segments;
  }, [segments, selectedEventType, selectedResourceId]);

  const canSchedule = scheduling != null && isSchedulableInteractionType(selectedEventType);
  const schedulingDurationSeconds =
    scheduling?.durationSeconds ?? (videoDuration > 0 ? Math.floor(videoDuration) : null);
  const scheduledWindows = useMemo(
    () =>
      isSchedulableInteractionType(selectedEventType) && selectedResourceId != null
        ? getScheduledWindows(
            evergreenTimeline,
            selectedEventType,
            selectedResourceId,
            schedulingDurationSeconds,
          )
        : [],
    [evergreenTimeline, selectedEventType, selectedResourceId, schedulingDurationSeconds],
  );

  return (
    <Accordion type="single" collapsible className="bg-medium  rounded-b-2xl">
      <AccordionItem value="item-1" className="p-0">
        <AccordionTrigger className="rounded-[20px] px-4">
          {t('timeCodeForm.title')}
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-4 pt-4 pb-4 px-4 overflow-visible">
          <div
            className={cn(
              'flex flex-col gap-4',
              locked && 'pointer-events-none select-none opacity-90',
            )}
          >
            <video
              ref={videoRef}
              className="sr-only"
              aria-hidden
              tabIndex={-1}
              preload="metadata"
              src={videoSrc}
              onError={onVideoError}
              muted
              playsInline
              loop
              onTimeUpdate={() => videoRef.current && setVideoTime(videoRef.current.currentTime)}
              onLoadedMetadata={() =>
                videoRef.current && setVideoDuration(videoRef.current.duration)
              }
            />

            <div className="flex items-center w-full justify-between gap-6 overflow-visible">
              <div className="flex-1 shrink-0 relative min-w-0 overflow-visible">
                <div className="relative w-full h-2 rounded-md bg-muted ">
                  {videoDuration > 0 &&
                    segmentsForResource.map((segment) => {
                      const leftPercent = (segment.startSeconds / videoDuration) * 100;
                      const widthPercent =
                        ((segment.endSeconds - segment.startSeconds) / videoDuration) * 100;
                      if (!segment.instant && widthPercent <= 0) return null;

                      return (
                        <div
                          key={segment.id}
                          className="absolute inset-y-0 min-w-[3px] h-4 top-1/2 -translate-y-1/2 bg-accent rounded-sm pointer-events-none z-1 opacity-90"
                          style={{
                            left: `${leftPercent}%`,
                            width: `${widthPercent}%`,
                          }}
                          title={
                            segment.instant
                              ? secondsToTimeString(segment.startSeconds)
                              : `${secondsToTimeString(segment.startSeconds)} → ${secondsToTimeString(segment.endSeconds)}`
                          }
                        />
                      );
                    })}
                </div>
                <div className="flex items-center justify-between w-full mt-3">
                  <div className="text-sm text-secondary">{secondsToTimeString(videoTime)}</div>
                  <div className="text-sm text-secondary">{secondsToTimeString(videoDuration)}</div>
                </div>
              </div>
            </div>
            {canSchedule && selectedResourceId == null ? (
              <div className="text-sm text-secondary py-2">
                {t('timeCodeForm.saveResourceFirst')}
              </div>
            ) : canSchedule && selectedResourceId != null ? (
              <ScheduledWindowsEditor
                workspaceId={scheduling.workspaceId}
                webinarId={scheduling.webinarId}
                type={selectedEventType}
                resourceId={selectedResourceId}
                windows={scheduledWindows}
                videoDurationSeconds={schedulingDurationSeconds}
                readOnly={locked}
              />
            ) : (
              <div className="flex flex-col gap-4">
                {segmentsForResource.length === 0 ? (
                  <div className="text-sm text-secondary py-2">{t('timeCodeForm.noSlot')}</div>
                ) : (
                  segmentsForResource.map((segment, index) => (
                    <div key={segment.id} className="flex flex-col gap-4">
                      {index > 0 && <Separator className="my-1" />}
                      <div className="flex flex-col md:flex-row items-center justify-between w-full gap-6 rounded-lg">
                        <HmsInputs
                          label={t('time.startLabel')}
                          value={secondsToHms(segment.startSeconds)}
                        />
                        <HmsInputs
                          label={t('time.endLabel')}
                          value={secondsToHms(segment.endSeconds)}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
